import dayjs from 'dayjs';
import 'dayjs/locale/en';
import 'dayjs/locale/ja';
import 'dayjs/locale/ko';
import 'dayjs/locale/zh-cn';
import duration from 'dayjs/plugin/duration';
import relativeTime from 'dayjs/plugin/relativeTime';
import utc from 'dayjs/plugin/utc';

// Extend dayjs plugins
dayjs.extend(relativeTime);
dayjs.extend(duration);
dayjs.extend(utc);

/**
 * Format date
 * @param date Date object or string
 * @param format Format template
 * @param locale Language locale
 * @returns Formatted date string
 */
export const formatDate = (
  date: Date | string | number,
  format: string = 'YYYY-MM-DD HH:mm:ss',
  locale: string = 'ko'
): string => {
  dayjs.locale(locale);
  return dayjs(date).format(format);
};

/**
 * Format relative time
 * @param date Date object or string
 * @param locale Language locale
 * @returns Relative time string
 */
export const formatRelativeTime = (
  date: Date | string | number,
  locale: string = 'ko'
): string => {
  dayjs.locale(locale);
  return dayjs(date).fromNow();
};

/**
 * Calculate date difference
 * @param start Start date
 * @param end End date
 * @returns Date difference object
 */
export const getDateDiff = (
  start: Date | string | number,
  end: Date | string | number
) => {
  const diff = dayjs(end).diff(dayjs(start));
  const duration = dayjs.duration(diff);

  return {
    years: duration.years(),
    months: duration.months(),
    days: duration.days(),
    hours: duration.hours(),
    minutes: duration.minutes(),
    seconds: duration.seconds(),
    milliseconds: duration.milliseconds(),
    totalMilliseconds: diff,
  };
};

/**
 * Check if date is today
 * @param date Date object or string
 * @returns Whether it is today
 */
export const isToday = (date: Date | string | number): boolean => {
  return dayjs(date).isSame(dayjs(), 'day');
};

/**
 * Check if date is yesterday
 * @param date Date object or string
 * @returns Whether it is yesterday
 */
export const isYesterday = (date: Date | string | number): boolean => {
  return dayjs(date).isSame(dayjs().subtract(1, 'day'), 'day');
};

/**
 * Add date
 * @param date Date object or string
 * @param value Value to add
 * @param unit Unit
 * @returns Date after addition
 */
export const addDate = (
  date: Date | string | number,
  value: number,
  unit: dayjs.ManipulateType
): Date => {
  return dayjs(date).add(value, unit).toDate();
};

/**
 * Subtract date
 * @param date Date object or string
 * @param value Value to subtract
 * @param unit Unit
 * @returns Date after subtraction
 */
export const subtractDate = (
  date: Date | string | number,
  value: number,
  unit: dayjs.ManipulateType
): Date => {
  return dayjs(date).subtract(value, unit).toDate();
};
